<script setup lang="ts">
import { useMediaQueryStore } from '@/stores/media_query_store';
import { ImageMedia } from '@clovyr/bed';
import type { Logo } from '@clovyr/bed/components';
import AppLogo from '@clovyr/bed/components/elements/AppLogo.vue';
import { storeToRefs } from 'pinia';

withDefaults(
  defineProps<{
    appLogo: Logo;
    headingText: string;
    appTitle: string;
    subheadingText?: string;
    subheadingText2?: string;
    rightImageSrc?: string;
  }>(),
  {
    headingText: '',
    appTitle: '',
    subheadingText: '',
    subheadingText2: '',
    rightImageSrc: '',
  },
);

const { isMobile } = storeToRefs(useMediaQueryStore());

const extraClass = computed(() => {
  return isMobile.value ? 'container' : '';
});
</script>

<template>
  <div class="app-launcher-hero" :class="extraClass">
    <AppLogo class="app-launcher-hero__app-icon" size="small" :logo="appLogo">
      <template #title>
        <div class="app-launcher-title">{{ appTitle }}</div>
      </template>
    </AppLogo>

    <div class="hero-masthead">
      <div class="hero-masthead__left">
        <h1 v-if="headingText" class="hero-heading">
          {{ headingText }}
        </h1>

        <div v-if="subheadingText" class="hero-subheading">
          {{ subheadingText }}
        </div>

        <div
          v-if="subheadingText2"
          class="hero-subheading"
          id="subheading-2"
          v-html="subheadingText2"
        ></div>
      </div>

      <div v-if="rightImageSrc" class="hero-masthead__right">
        <ImageMedia :filename="rightImageSrc" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/fonts';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.app-launcher-hero {
  @include breakpoints.media-breakpoint-up(md) {
    height: grid-vars.space(20);
  }

  &__app-icon {
    margin-bottom: 2rem;

    @include breakpoints.media-breakpoint-up(sm) {
      margin-bottom: 0.5rem;
    }
  }

  :deep(.app-launcher-title) {
    @include fonts.font-size(24);

    font-weight: 600;
    color: colors.color(grey, secondary);
  }

  .hero-masthead {
    display: flex;
    justify-content: space-between;

    &__right {
      :deep(.image-media) {
        height: 7rem;
        transform: translateY(-1rem);

        @include breakpoints.media-breakpoint-up(sm) {
          height: 10rem;
          transform: translateY(-3rem);
          margin-right: 1rem;
        }
      }
    }
  }

  .hero-heading {
    @include fonts.font-size(30);

    @include breakpoints.media-breakpoint-up(md) {
      @include fonts.font-size(40);
    }

    @include breakpoints.media-breakpoint-up(xl) {
      @include fonts.font-size(50);
    }

    padding: 20px 0 0 0;

    @include breakpoints.media-breakpoint-up(sm) {
      padding-left: 1rem;
    }
  }

  .hero-subheading {
    @include fonts.font-size(18);

    @include breakpoints.media-breakpoint-up(md) {
      @include fonts.font-size(24);
    }

    margin-bottom: grid-vars.space(2.4);
  }
}
</style>
